<template>
  <v-btn
    v-bind="$attrs"
    icon
    :disabled="!project" :loading="downloadStatus.pending || addNotificationStatus.pending"
    @click="download"
  >

    <snackbar persistent v-model="downloadStatus.pending">Downloading…</snackbar>

    <error-dialog :status="downloadStatus" />
    <error-dialog :status="addNotificationStatus" />

    <sign-in-dialog ref="sign-in" v-model="showSignIn">
      <template v-slot:title>Verify your email address</template>
      <template v-slot:subtitle>We'll send you an email when this project is ready to download / share</template>
    </sign-in-dialog>

    <dialog-scroll-lock v-if="showNotReady" v-model="showNotReady" width="500">
      <v-card class="overflow-x-hidden">
        <v-btn class="float-right ma-3" icon @click="showNotReady = false">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>Download not ready</v-card-title>
        <v-divider />
        <div v-if="project.locked" class="pa-6">
          <p>We're still building a video file that you can download / share for this project.</p>
          <div class="mb-5 d-flex align-baseline">
            <div class="grey--text overline short mr-2">Started</div>
            <div style="word-wrap: normal;">{{project.render.started_at | moment}}</div>
          </div>
          <div v-if="willNotify">We will email you when it's ready.</div>
          <div v-else>Do you want us to notify you when it's ready?</div>
        </div>
        <div v-else-if="purchaseDialog" class="pa-6">
          You'll be able to download this video after you check out and lock the project. See our <a target="_blank" href="https://ellacard.com/faq#can-i-download-my-video">FAQ</a> for more details.
        </div>
        <div v-else class="pa-6">
          <!-- This will happen in the case when someone previews an order (a valid case) -->
          This video project has not been locked – we can't build a video file that you can download / share until {{globalUserId == project.user_id ? 'you lock' : 'the organizer locks'}} the project. See our <a target="_blank" href="https://ellacard.com/faq#group-video-lock">FAQ</a> for more details.
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <template v-if="project.locked && !willNotify">
            <v-btn @click="showNotReady = false">No</v-btn>
            <v-btn color="primary" :loading="addNotificationStatus.pending" @click="addNotification">Yes</v-btn>
          </template>
          <v-btn v-else @click="showNotReady = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <v-icon v-html="mdiDownload" />
  </v-btn>
</template>

<script>
import ErrorDialog from '@/components/ErrorDialog.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { OPError } from '@/utils/op-dev-fetch';
import { mdiClose, mdiDownload } from '@mdi/js';

export default {
  name: 'download-group-video-btn',

  components: {
    ErrorDialog,
    SignInDialog
  },

  props: [ 'project', 'purchaseDialog' ],

  data() {
    return {
      showSignIn: false,
      showNotReady: false,
      downloadStatus: this.$track('download'),
      addNotificationStatus: this.$track('addNotification')
    };
  },

  computed: {
    src() {
      const p = this.project;
      return p && p.render && !p.render.failed && p.render.finished_at && p.render.finished_at > p.last_visible_update_at && p.render.url;
    },

    willNotify() {
      return this.project && (this.globalUserId in (this.project.render_notify || {}));
    }
  },

  created() {
    Object.assign(this, { mdiClose, mdiDownload });
  },

  methods: {
    async download() {
      if (!this.src) {
        this.showNotReady = true;
        return;
      }

      // TODO: build a progress indicator
      // https://javascript.info/fetch-progress
      const url = URL.createObjectURL(await fetch(this.src).then(r => r.blob()));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'video.mp4';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    async addNotification() {
      if (!this.isSignedIn) {
        await this.$refs['sign-in'].prompt();
        if (!this.isSignedIn)
          return false;
      }
      const params = { project_id: this.projectId, url: window.location.href };
      let isComplete = false;
      try {
        const notify = await this.$call('add-render-notification', params);
        this.$emit('update-notify', notify);
      } catch (e) {
        if (e instanceof OPError && e.type == 'RenderComplete')
          isComplete = true;
        else
          throw e;
      }
      this.showNotReady = false;
      if (isComplete) {
        this.$confirm({
          title: 'Video already complete',
          msg: `This video is already complete and ready to download / share (we likely just finished)`,
          buttons: [ 'Ok' ],
          primary: 'Ok',
          persistent: false
        });
        this.$emit('reload');
      }
    }
  }
}
</script>
